import camelcaseKeys from 'camelcase-keys';
import { defineStore } from 'pinia';
import { throwErrorMessage } from 'dashboard/store/utils/api';
import PipelinesAPI from 'dashboard/api/pipelinesApi';

const camelize = data => camelcaseKeys(data || {}, { deep: true });

export const usePipelinesStore = defineStore('pipelines', {
  state: () => ({
    records: [],
    uiFlags: {
      fetchingList: false,
      creatingItem: false,
      updatingItem: false,
      deletingItem: false,
    },
  }),

  getters: {
    getPipelinesList: state => state.records,
    getPipelineById: state => id => state.records.find(p => p.id === id),
    getFirstPipeline: state => state.records[0] || null,
  },

  actions: {
    setUIFlag(flags) {
      this.uiFlags = { ...this.uiFlags, ...flags };
    },

    upsertPipeline(record) {
      const index = this.records.findIndex(r => r.id === record.id);
      if (index === -1) this.records.push(record);
      else this.records[index] = record;
    },

    async get() {
      this.setUIFlag({ fetchingList: true });
      try {
        const { data } = await PipelinesAPI.get();
        this.records = camelize(data.payload || []);
        return this.records;
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ fetchingList: false });
      }
    },

    async create(attrs) {
      this.setUIFlag({ creatingItem: true });
      try {
        const { data } = await PipelinesAPI.create(attrs);
        const pipeline = camelize(data.payload);
        this.upsertPipeline(pipeline);
        return pipeline;
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ creatingItem: false });
      }
    },

    async update({ id, ...attrs }) {
      this.setUIFlag({ updatingItem: true });
      try {
        const { data } = await PipelinesAPI.update(id, attrs);
        const pipeline = camelize(data.payload);
        this.upsertPipeline(pipeline);
        return pipeline;
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ updatingItem: false });
      }
    },

    async delete(id, params = {}) {
      this.setUIFlag({ deletingItem: true });
      try {
        await PipelinesAPI.destroy(id, params);
        this.records = this.records.filter(r => r.id !== Number(id));
        return Number(id);
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ deletingItem: false });
      }
    },

    async createStage(pipelineId, attrs) {
      try {
        const { data } = await PipelinesAPI.createStage(pipelineId, attrs);
        const stage = camelize(data.payload);
        const pipeline = this.records.find(p => p.id === Number(pipelineId));
        if (pipeline) {
          pipeline.stages = [...(pipeline.stages || []), stage];
        }
        return stage;
      } catch (error) {
        return throwErrorMessage(error);
      }
    },

    async updateStage(pipelineId, stageId, attrs) {
      try {
        const { data } = await PipelinesAPI.updateStage(
          pipelineId,
          stageId,
          attrs
        );
        const stage = camelize(data.payload);
        const pipeline = this.records.find(p => p.id === Number(pipelineId));
        if (pipeline) {
          const idx = (pipeline.stages || []).findIndex(
            s => s.id === Number(stageId)
          );
          if (idx !== -1) pipeline.stages[idx] = stage;
        }
        return stage;
      } catch (error) {
        return throwErrorMessage(error);
      }
    },

    async deleteStage(pipelineId, stageId) {
      try {
        await PipelinesAPI.destroyStage(pipelineId, stageId);
        const pipeline = this.records.find(p => p.id === Number(pipelineId));
        if (pipeline) {
          pipeline.stages = (pipeline.stages || []).filter(
            s => s.id !== Number(stageId)
          );
        }
        return Number(stageId);
      } catch (error) {
        return throwErrorMessage(error);
      }
    },
  },
});
